import { Browser } from "../../core/browser";
import { ChipsideComponent } from "../chipside-component";

export class Button extends ChipsideComponent {
	#Mappers = {};
	static observedAttributes = ["icon", "button-style", "variation", "size", "value", "orientation", "link", "active", "suffix-icon", "button-class"];

	constructor() {
		super();

		this.icon = this.getAttribute("icon") ?? "";
		this.variation = this.getAttribute("variation") ?? "theme";
		this.description = this.getAttribute("description") ?? "";
		this.buttonStyle = this.getAttribute("button-style") ?? "";
		this.buttonClass = this.getAttribute("button-class") ?? "";
		this.identifier = this.getAttribute("identifier") ?? "";
		this.size = this.getAttribute("size") ?? "";
		this.active = this.hasAttribute("active");
		this.suffixIcon = this.getAttribute("suffix-icon");
		this.value = this.getAttribute("value");
		this.link = this.getAttribute("link") ?? "";
		this.submits = this.hasAttribute("submits");
		this.orientation = this.getAttribute("orientation") ?? "";
		this.stepDirection = this.getAttribute("step-direction") ?? "";

		this.#Mappers = {
			form: this.getAttribute("form") ?? "",
			download: this.getAttribute("download") ?? "",
			type: this.getAttribute("type") ?? "button",
			target: this.getAttribute("target") ?? "",
			formaction: this.getAttribute("formaction") ?? "",
			formmethod: this.getAttribute("formmethod") ?? ""
		};

		Object.keys(this.#Mappers).forEach(key => this[key] = this.#Mappers[key]);
	}

	parsedCallback() {
		const button = document.createElementWithContents(this.link ? "a" : "button", this.childNodes.length ? document.createElementWithContents("span", this.childNodes) : null, {
			className: "btn--base"
		});

		button.classList.add(Button.ClassFromVariation(this.variation))

		if (this.identifier) { button.id = this.identifier; }
		if (this.stepDirection) { button.addEventListener("click", () => this.#HandleStepButtonClick()); }
		if (this.active) { button.classList.add("active"); }
		if (this.buttonClass) { button.classList.add(...this.buttonClass.split(" ")); }

		if (this.tooltip && button.textContent.trim() === "") { button.ariaLabel = this.tooltip; }
		if (this.description) { button.ariaLabel = this.description; }
		if (this.submits) { button.classList.add("btn-entity-post"); }
		if (this.link) { button.href = Browser.ResolveRelativeURL(this.link); }
		if (this.size) { button.classList.add(`btn--${this.size.toLowerCase()}`); }
		if (this.buttonStyle) { button.classList.add(`btn--${this.buttonStyle.toLowerCase()}`); }
		if (this.orientation) { button.classList.add(`btn--${this.orientation.toLowerCase()}`); }

		if (this.icon) {
			button.prepend(document.createElementWithContents("i", null, {
				className: this.icon
			}));
		}

		if (this.suffixIcon) {
			button.appendChild(document.createElementWithContents("i", null, {
				className: this.suffixIcon
			}));
		}

		Object.keys(this.#Mappers).filter(x => this.#Mappers[x])
			.forEach(key => button.setAttribute(key, this.#Mappers[key] === true ? "" : this.#Mappers[key]));

		this.appendChild(button);
	}

	#HandleStepButtonClick() {
		const tabContent = this.closest(".tab-group__tab");

		let index = [...this.closest(".tab-group__content")?.children ?? []].indexOf(tabContent);

		if (this.stepDirection.toLowerCase() === "next") { index = Math.min(index + 1, this.closest(".tab-group__content").children.length - 1); }
		else { index = Math.max(index - 1, 0); }

		const tab = this.closest("chip-tabgroup")?.querySelector(`:scope > .tab-group__tabs > .tab__wrapper > chip-tab:nth-child(${index + 1})`);

		if (tab && (this.closest("chip-tabgroup")?.querySelector(`:scope > .tab-group__tabs > .tab__wrapper > chip-tab:nth-child(${index})`)?.checkValidity() === true || this.stepDirection.toLowerCase() !== "next")) {
			tab.selected = true;
		}
	}

	click() {
		this.querySelector(":scope > button, :scope > a")?.click();
	}

	attributeChanged(name, oldValue, newValue) {
		switch (name) {
			case "variation":
				this.querySelectorAll(":scope>button, :scope>a, :scope>chip-dropdown>div>button").forEach(x => {
					x.classList.remove(Button.ClassFromVariation(oldValue));
					x.classList.add(Button.ClassFromVariation(newValue));
				});
				break;
			case "size":
				this.querySelectorAll(":scope>button, :scope>a, :scope>chip-dropdown>div>button").forEach(x => {
					x.classList.remove(`btn--${this.size}`);
					x.classList.add(`btn--${newValue.toLowerCase()}`);
				});
				break;
			case "button-style":
				this.querySelectorAll(":scope>button, :scope>a, :scope>chip-dropdown>div>button").forEach(x => {
					x.classList.remove(`btn--${oldValue}`);
					x.classList.add(`btn--${newValue.toLowerCase()}`);
				});
				break;
			case "button-class":
				{
					const buttons = this.querySelectorAll(":scope>button, :scope>a, :scope>chip-dropdown>div>button");

					if (oldValue) { buttons.forEach(x => x.classList.remove(...oldValue.split(" "))); }
					if (newValue) { buttons.forEach(x => x.classList.add(...newValue.split(" "))); }
				}
				break;
			case "orientation":
				this.firstElementChild.classList.remove(`btn--${this.orientation}`);
				this.firstElementChild.classList.add(`btn--${newValue.toLowerCase()}`);
				break;
			case "tooltip":
				{
					const target = this.querySelector("button, a");

					if (target?.textContent.trim() === "") {
						target.ariaLabel = newValue;
					}
				}
				break;
			case "icon":
				{
					const existingIcon = this.querySelector("i");

					if (existingIcon) {
						existingIcon.className = newValue;
					} else {
						this.prepend(document.createElementWithContents("i", null, { className: newValue }));
					}
				}
				break;
			case "suffix-icon":
				{
					const existingIcon = this.querySelector(":scope > div > button > i:last-child:not(:first-child)");

					if (existingIcon) {
						existingIcon.className = newValue;
					} else {
						this.querySelector(":scope > div > button").appendChild(document.createElementWithContents("i", null, { className: newValue }));
					}
				}
				break;
			case "link":
				{
					const anchor = this.querySelector("a");

					if (anchor) {
						if (newValue) {
							anchor.href = newValue;
						} else {
							anchor.removeAttribute("href");
						}
					}
				}
				break;
			case "disabled":
				this.querySelectorAll(":scope>button, :scope>a, :scope>chip-dropdown>div>button").forEach(x => {
					if (this.IsAttributeValueTrue(newValue)) {
						x.disabled = true;
					} else {
						x.removeAttribute("disabled");
					}
				});
				break;
			case "active":
				if (this.IsAttributeValueTrue(newValue)) {
					this.querySelector("button, a").classList.add("active");
				} else {
					this.querySelector("button, a").classList.remove("active");
				}
				break;
		}
	}

	focus() {
		this.querySelector("a, button")?.focus();
	}

	static ClassFromVariation(variation) {
		switch (variation?.toLowerCase()) {
			case "theme-outline":
			case "complementary-outline":
			case "info-outline":
			case "success-outline":
			case "warning-outline":
			case "secondary-outline":
			case "danger-outline":
			case "theme-secondary":
			case "theme-tertiary":
			case "secondary-secondary":
			case "danger-secondary":
			case "danger-tertiary":
			case "warning-secondary":
			case "warning-tertiary":
			case "secondary-tertiary":
			case "success-secondary":
			case "info":
			case "complementary":
			case "complementary-secondary":
			case "complementary-tertiary":
			case "info-secondary":
			case "info-tertiary":
			case "body":
			case "success-tertiary":
			case "pagination":
			case "secondary":
			case "none":
			case "theme": return `btn-${variation.toLowerCase()}`;

			case "success":
			case "warning":
			case "danger": return `btn-${variation.toLowerCase()}-action`;
			default: return "btn-theme";
		}
	}
}